import { useRef, useState, useEffect } from 'react';
import FieldEditIndicator from '../FieldEditIndicator';
import { useFieldEditIndicator } from '../hooks/useFieldEditIndicator';
import {
  Box,
  Flex,
  Icon,
  Input,
  Text,
  Image,
  useDisclosure,
  Spinner,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { CustomUploadIcon } from '../../../../theme/icons/CustomUploadIcon';
import { useAppDispatch, useAppSelector } from '../../../../store/redux/hooks';
import { setFormField } from '../../../../store/redux/slices/form.slice';
import { useTemplateFieldValidation } from '../validation/useTemplateFieldValidation';
import CropModal from '../CropModal';
import { FormState } from '../../../../store/types/form';

interface FormImageUploadProps {
  label: string;
  field: 'formLogoImage' | 'formFaviconImage';
  recommendation?: string;
  aspectRatio?: number;
  isDisabled?: boolean;
}

const FormImageUpload = ({
  label,
  field,
  recommendation = 'Recommended PNG file type with transparent background.',
  aspectRatio = 360 / 110,
  isDisabled = false,
}: FormImageUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const imageUploadRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap: { [field]: imageUploadRef },
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { handleFocus, handleBlur } = useTemplateFieldValidation('form');
  const storedImage = useAppSelector((s) => s.form[field]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(storedImage || null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setPreview(storedImage || null);
  }, [storedImage]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsLoading(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setSelectedImage(base64);
      setIsLoading(false);
      onOpen();
    };
    reader.onerror = () => {
      setIsLoading(false);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleCropComplete = (croppedBase64: string) => {
    setPreview(croppedBase64);
    dispatch(
      setFormField({ field: field as keyof FormState, value: croppedBase64 })
    );
    onClose();
  };

  const handleClick = () => {
    if (isDisabled) return;
    handleFocus(field);
    inputRef.current?.click();
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreview(null);
    dispatch(setFormField({ field: field as keyof FormState, value: '' }));
  };

  return (
    <FormControl w="50%">
      <FormLabel variant="templateBuilderLabel" mb={2}>
        {label}
      </FormLabel>
      <FieldEditIndicator
        fieldRef={imageUploadRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-40px"
        top="50px"
      >
        <Box
          position="relative"
          border="1px dashed"
          bg="softWhite"
          borderColor="grey"
          borderRadius="10px"
          p={3}
          w="90%"
          h="100px"
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          opacity={isDisabled ? 0.6 : 1}
          pointerEvents={isDisabled ? 'none' : 'auto'}
          transition="all 0.2s ease"
          _hover={isDisabled ? {} : { borderColor: 'orangePrimary' }}
          onClick={handleClick}
          tabIndex={isDisabled ? -1 : 0}
          onFocus={() => !isDisabled && handleFocus(field)}
          onBlur={handleBlur}
        >
          <Flex
            direction="column"
            align="center"
            justify="center"
            textAlign="center"
            h="full"
            gap={1}
          >
            {isLoading ? (
              <Spinner size="sm" />
            ) : preview ? (
              <>
                <Image
                  src={preview}
                  alt={`${label} preview`}
                  maxH="60px"
                  objectFit="contain"
                  borderRadius="5px"
                />
              </>
            ) : (
              <Box>
                <Icon
                  as={CustomUploadIcon}
                  boxSize="20px"
                  color="orangePrimary"
                  mb={1}
                />
                <Text fontSize="13px">Click to upload</Text>
              </Box>
            )}
          </Flex>
        </Box>
      </FieldEditIndicator>

      <Input
        ref={inputRef}
        type="file"
        accept=".png, .jpg, .jpeg"
        onChange={handleFileChange}
        display="none"
        name={`form.${field}`}
      />

      <Text fontSize="12px" mt={3} color="inkBlue">
        {recommendation}
      </Text>

      {selectedImage && (
        <CropModal
          isOpen={isOpen}
          onClose={onClose}
          imageSrc={selectedImage}
          aspect={aspectRatio}
          platform="shared"
          field="logo"
          onCropComplete={handleCropComplete}
        />
      )}
    </FormControl>
  );
};

export default FormImageUpload;
